import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ButtonComponent,
  IOptionItem,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  SelectComponent,
  SvgIconComponent,
  TableComponent,
  ToastService,
  MultiselectComponent,
  SkeletonDirective,
  IconButtonComponent,
} from '@ui-lib/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, Observable, take } from 'rxjs';
import { LoadingState } from '../../shared/types/loading.type';
import { AgentsService } from './agents.service';
import { AGENTS_TABLE_COLUMNS } from './agents.consts';
import { ConfirmationDialogComponent } from '../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { AgentsModalComponent } from './agents-modal/agents-modal.component';
import { AgentsFiltersForm, ISearchAgentsItem, ModalAction } from './agents.model';
import { FacilitySelectorComponent } from '../../shared/components/facility-selector/facility-selector.component';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { AgentStatuses } from '../../core/openapi/bff-admin-api';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';

@Component({
  standalone: true,
  selector: 'mon-agents',
  imports: [
    CommonModule,
    TranslateModule,
    AsyncPipe,
    ReactiveFormsModule,
    TableComponent,
    SvgIconComponent,
    SelectComponent,
    PaginationComponent,
    ButtonComponent,
    FacilitySelectorComponent,
    MultiselectComponent,
    SkeletonDirective,
    IconButtonComponent,
    PageToolbarComponent,
  ],
  providers: [AgentsService],
  templateUrl: './agents.component.html',
  styleUrls: ['./agents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentsComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  protected readonly agentsService = inject(AgentsService);

  protected readonly filterForm = new FormGroup<AgentsFiltersForm>({
    keyword: new FormControl<string>('', { nonNullable: true }),
    facilityIds: new FormControl<string[]>([], { nonNullable: true }),
    statusIds: new FormControl<number[]>([], { nonNullable: true }),
    approval: new FormControl<IOptionItem<boolean> | null>(null),
  });

  protected readonly AGENTS_TABLE_COLUMNS = AGENTS_TABLE_COLUMNS;
  protected readonly LoadingState = LoadingState;
  protected readonly ModalAction = ModalAction;
  protected readonly currentPage = signal<number>(1);
  protected readonly canManageAgents = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.AgentManage),
    { initialValue: false },
  );
  protected readonly canReadFacilities = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FacilityRead),
    { initialValue: false },
  );
  protected readonly canReadScans = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.ScansRead),
    { initialValue: false },
  );
  protected get canEditAgentFacility(): boolean {
    return this.canManageAgents() && this.canReadFacilities();
  }

  private readonly allStatuses = toSignal(this.agentsService.statuses$);

  protected selectedFacilities: IDictionaryItem<string>[] = [];

  protected readonly approvalOptions$: Observable<IOptionItem<boolean>[]> = this.translateService
    .stream([
      'monitoring.agents.filters.approval.approved',
      'monitoring.agents.filters.approval.unapproved',
    ])
    .pipe(
      map((translations) => [
        {
          id: true,
          label: translations['monitoring.agents.filters.approval.approved'],
        },
        {
          id: false,
          label: translations['monitoring.agents.filters.approval.unapproved'],
        },
      ]),
    );

  public ngOnInit(): void {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters) => {
      const facilityIds =
        this.canReadFacilities() && (filters.facilityIds?.length ?? 0) > 0
          ? filters.facilityIds
          : null;
      const statusIds = (filters.statusIds?.length ?? 0) > 0 ? filters.statusIds : null;

      this.agentsService.updateAgentRequest({
        skip: 0,
        keyword: filters.keyword,
        facilityIds,
        statusIds,
        isApproved: filters.approval?.id,
      });
      this.currentPage.set(1);
    });
  }

  protected get selectedStatuses(): { id: number; name: string }[] {
    const selectedIds = new Set(this.filterForm.controls.statusIds.value);
    return this.allStatuses()?.filter((s) => s.id != null && selectedIds.has(s.id)) ?? [];
  }

  protected get isFiltersActive(): boolean {
    const { keyword, facilityIds, statusIds, approval } = this.filterForm.value;
    return !!(
      keyword ||
      (this.canReadFacilities() && (facilityIds?.length ?? 0) > 0) ||
      (statusIds?.length ?? 0) > 0 ||
      approval
    );
  }

  protected get selectedFiltersCount(): number {
    return (
      this.selectedFacilities.length +
      this.selectedStatuses.length +
      (this.filterForm.controls.approval.value ? 1 : 0)
    );
  }

  public get facilityIdsControl(): FormControl<string[]> {
    return this.filterForm.get('facilityIds') as FormControl<string[]>;
  }

  public get searchControl(): FormControl<string> {
    return this.filterForm.controls.keyword;
  }

  public onFacilitiesSelectionChange(items: IDictionaryItem<string>[]): void {
    this.selectedFacilities = items;
  }

  public getStatusLabel(status: AgentStatuses | undefined): string {
    return status?.toLocaleLowerCase() ?? '';
  }

  public clearAllFilters(): void {
    this.filterForm.patchValue({
      keyword: '',
      facilityIds: [],
      statusIds: [],
      approval: null,
    });
  }

  public removeFacilityFilter(id: string): void {
    this.filterForm.controls.facilityIds.setValue(
      this.filterForm.controls.facilityIds.value.filter((v) => v !== id),
    );
  }

  public removeStatusFilter(id: number): void {
    this.filterForm.controls.statusIds.setValue(
      this.filterForm.controls.statusIds.value.filter((v) => v !== id),
    );
  }

  public removeApprovalFilter(): void {
    this.filterForm.controls.approval.setValue(null);
  }

  public asAgent = (agent: unknown): ISearchAgentsItem => agent as ISearchAgentsItem;

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentPage.set(paginator.pageIndex);
    this.agentsService.updateAgentRequest({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public confirmDeleteModal(agent: ISearchAgentsItem): void {
    if (!this.canManageAgents()) {
      return;
    }

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        data: {
          headerTranslationKey: 'monitoring.agents.dialog.delete.title',
          descriptionTranslationKey: 'monitoring.agents.dialog.delete.description',
          descriptionTranslationParams: { name: agent.name },
          approvalTranslationKey: 'monitoring.agents.dialog.delete.confirm',
          cancellationTranslationKey: 'monitoring.agents.dialog.delete.cancel',
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.handleAgentDelete(agent.id);
      });
  }

  public navigateToScanner(agent: ISearchAgentsItem): void {
    if (!this.canReadScans()) {
      return;
    }

    void this.router.navigate(['/scanner'], {
      queryParams: {
        facilityId: agent.facilityId ?? null,
        agentId: agent.id,
      },
    });
  }

  private handleAgentDelete(agentId: string): void {
    if (!this.canManageAgents()) {
      return;
    }

    this.agentsService
      .deleteAgent(agentId)
      .pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.agents.toast.success.agent-deleted'),
        });

        this.agentsService.reloadAgents();
      });
  }

  public openAgentModal(agentId: string, agentName: string, action: ModalAction): void {
    if (!this.canEditAgentFacility) {
      return;
    }

    this.modalService
      .open({
        component: AgentsModalComponent,
        data: {
          agentId,
          agentName,
          action,
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toastService.success({
          message: this.translateService.instant(`monitoring.agents.toast.success.agent-${action}`),
        });
        this.agentsService.reloadAgents();
      });
  }
}
